/**
 * ForderungsTagesbriefing — Tages-Assistent-Popup (Forderungen / Buchhaltung)
 *
 * Zeigt einmal täglich ab 08:00 den Tagesbericht des Buchhaltungs-Agenten:
 *   • Übersicht / Sachstände
 *   • Antwort-Vorschläge (freigabebereite E-Mail-Entwürfe inkl. Text + Anhänge,
 *     direkt versendbar via „Freigeben & Senden"; plus Agenten-Vorschlagstexte)
 *   • Pipeline (gestern raus / heute geplant / Kommentar + was fehlt)
 *   • Versand (kurze Übersicht: was gestern/heute rausging)
 *   • Briefing (aktuelles Briefing + „Anpassung vorschlagen")
 *   • Optimierung & Entwicklung (optimierungs-/einrichtungs-/entwicklungsvorschlaege)
 *
 * Unten: Rückmeldung an den Agenten → POST /api/forderung-tagesbericht.
 *
 * Daten: GET /api/forderung-tagesbericht (neuester agenten_protokoll-Eintrag
 * mit aktion='tagesbericht' + Live-Kennzahlen + freigabe_vorschlaege +
 * versand_uebersicht).
 *
 * Versand: POST /api/email-draft-send { id, userId } (Bearer-Token im Header).
 *
 * Auto-Öffnen: einmal pro Tag, wenn Uhrzeit >= 08:00 und heute noch nicht
 * gezeigt (localStorage 'tagesbriefing_last' = YYYY-MM-DD). NUR für den
 * Inhaber/Admin (useAuth().isAdmin) — für alle anderen Nutzer rendert die
 * Komponente null und öffnet nie.
 *
 * Design: Tailwind, Brand-Rot #9B1C1C, lucide-Icons. Scrollbar, mobil-tauglich.
 */

import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  X,
  Loader2,
  LayoutDashboard,
  MessageSquareReply,
  GitBranch,
  FileText,
  Sparkles,
  Send,
  CheckCircle2,
  Euro,
  Mail,
  Inbox,
  AlertCircle,
  Paperclip,
  ChevronDown,
  Truck,
  Clock,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import AlleMailsTab, { type AlleMail } from './forderungsmanager/AlleMailsTab';

const BRAND = '#9B1C1C';
const STORAGE_KEY = 'tagesbriefing_last';

// ─── Datentypen (aus details-JSON des Tagesberichts) ─────────────────────────

interface StrategieVorschlag {
  label?: string;
  text?: string;
  empfohlen?: boolean;
}

interface AntwortVorschlag {
  betreff?: string;
  absender?: string;
  vorschlag_text?: string;
  strategien?: StrategieVorschlag[];
}

interface Vorschlag {
  titel?: string;
  text?: string;
}

interface BerichtDetails {
  sachstaende?: string | string[];
  antwort_vorschlaege?: AntwortVorschlag[];
  gestern_versandt?: string | string[];
  heute_geplant?: string | string[];
  pipeline_kommentar?: string;
  optimierungsvorschlaege?: Array<Vorschlag | string>;
  einrichtungsvorschlaege?: Array<Vorschlag | string>;
  entwicklungsvorschlaege?: Array<Vorschlag | string>;
  dokumente_verbucht?: string | string[];
  offene_fragen_an_andreas?: string | string[];
  briefing?: string;
}

interface LiveKennzahlen {
  offene_forderungen_summe: number;
  offene_forderungen_anzahl: number;
  heute_versendet_anzahl: number;
  ready_for_review_anzahl: number;
}

// Freigabebereite E-Mail-Entwürfe (aus /api/forderung-tagesbericht → freigabe_vorschlaege)
interface FreigabeAnhang {
  filename: string;
}

interface FreigabeVorschlag {
  id: string;
  recipient?: string | null;
  cc?: string | null;
  subject?: string | null;
  body_html?: string | null;
  prioritaet?: string | number | null;
  akte_id?: string | null;
  anhaenge?: FreigabeAnhang[];
}

// Kurze Versandübersicht (aus /api/forderung-tagesbericht → versand_uebersicht)
interface VersandEintrag {
  recipient?: string | null;
  subject?: string | null;
  sent_at?: string | null;
}

interface VersandUebersicht {
  eintraege?: VersandEintrag[];
  gestern_anzahl?: number;
  heute_anzahl?: number;
}

interface BerichtRow {
  id?: string;
  beschreibung?: string | null;
  details?: BerichtDetails | null;
  erstellt_am?: string | null;
}

interface TagesberichtResponse {
  ok: boolean;
  bericht: BerichtRow | null;
  hinweis: string | null;
  live_kennzahlen: LiveKennzahlen;
  freigabe_vorschlaege?: FreigabeVorschlag[];
  versand_uebersicht?: VersandUebersicht;
  alle_mails?: AlleMail[];
}

type TabId =
  | 'uebersicht'
  | 'antworten'
  | 'alle-mails'
  | 'pipeline'
  | 'versand'
  | 'briefing'
  | 'entwicklung';

const TABS: Array<{ id: TabId; label: string; icon: typeof LayoutDashboard }> = [
  { id: 'uebersicht', label: 'Übersicht / Sachstände', icon: LayoutDashboard },
  { id: 'antworten', label: 'Antwort-Vorschläge', icon: MessageSquareReply },
  { id: 'alle-mails', label: 'Alle Mails', icon: Mail },
  { id: 'pipeline', label: 'Pipeline', icon: GitBranch },
  { id: 'versand', label: 'Versand', icon: Truck },
  { id: 'briefing', label: 'Briefing', icon: FileText },
  { id: 'entwicklung', label: 'Optimierung & Entwicklung', icon: Sparkles },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function todayKey(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/** Soll das Popup heute automatisch öffnen? (>= 08:00 und heute noch nicht gezeigt) */
function shouldAutoOpen(): boolean {
  try {
    const now = new Date();
    if (now.getHours() < 8) return false;
    const last = localStorage.getItem(STORAGE_KEY);
    return last !== todayKey();
  } catch {
    return false;
  }
}

function markShownToday(): void {
  try {
    localStorage.setItem(STORAGE_KEY, todayKey());
  } catch {
    /* localStorage nicht verfügbar — ignorieren */
  }
}

function asList(value: string | string[] | undefined | null): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return [value];
}

function vorschlagTitel(v: Vorschlag | string): string {
  return typeof v === 'string' ? v : v.titel || v.text || '';
}

function vorschlagText(v: Vorschlag | string): string | null {
  if (typeof v === 'string') return null;
  return v.titel && v.text ? v.text : null;
}

function eur(n: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n || 0);
}

/**
 * body_html sicher als reinen Text darstellen: HTML-Tags entfernen und
 * Entities entschärfen. Im Projekt existiert kein Sanitizer (kein DOMPurify),
 * daher wird KEIN HTML gerendert — der Text wird via whiteSpace:pre-wrap
 * angezeigt. Kein dangerouslySetInnerHTML → kein XSS-Risiko.
 */
function htmlZuText(html: string | null | undefined): string {
  if (!html) return '';
  return html
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\s*\/\s*(p|div|tr|li|h[1-6])\s*>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Uhrzeit (HH:MM) aus einem ISO-Zeitstempel; leer bei ungültigem Wert. */
function uhrzeit(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

// ─── UI-Bausteine ────────────────────────────────────────────────────────────

function Kennzahl({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Euro;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3">
      <div
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md"
        style={{ backgroundColor: `${BRAND}14`, color: BRAND }}
      >
        <Icon className="h-4.5 w-4.5" strokeWidth={2} />
      </div>
      <div className="min-w-0">
        <div className="truncate text-xs text-gray-500">{label}</div>
        <div className="text-base font-semibold text-gray-900">{value}</div>
      </div>
    </div>
  );
}

function ListBlock({ titel, items }: { titel: string; items: string[] }) {
  if (items.length === 0) return null;
  return (
    <div className="mb-4">
      <h4 className="mb-1.5 text-sm font-semibold text-gray-900">{titel}</h4>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="flex gap-2 text-sm text-gray-700">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: BRAND }} />
            <span className="whitespace-pre-wrap">{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Freigabe-Vorschlag-Karte (aufklappbar + „Freigeben & Senden") ───────────

function FreigabeKarte({
  vorschlag,
  onSend,
  sending,
}: {
  vorschlag: FreigabeVorschlag;
  onSend: (id: string) => void;
  sending: boolean;
}) {
  const [offen, setOffen] = useState(false);
  const text = htmlZuText(vorschlag.body_html);
  const anhaenge = vorschlag.anhaenge ?? [];

  return (
    <div className="rounded-xl border border-gray-200 p-4">
      <div className="mb-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-gray-900">
            {vorschlag.subject || 'Ohne Betreff'}
          </div>
          <div className="truncate text-xs text-gray-500">
            An: {vorschlag.recipient || 'Unbekannt'}
            {vorschlag.cc ? ` · CC: ${vorschlag.cc}` : ''}
          </div>
        </div>
        <button
          type="button"
          onClick={() => onSend(vorschlag.id)}
          disabled={sending}
          className="flex h-fit shrink-0 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-white transition disabled:opacity-60"
          style={{ backgroundColor: BRAND }}
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Freigeben &amp; Senden
        </button>
      </div>

      {anhaenge.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {anhaenge.map((a, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-0.5 text-xs text-gray-700"
            >
              <Paperclip className="h-3 w-3" />
              {a.filename}
            </span>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => setOffen((o) => !o)}
        className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700"
      >
        <ChevronDown
          className={`h-4 w-4 transition-transform ${offen ? 'rotate-180' : ''}`}
        />
        {offen ? 'Text ausblenden' : 'Text anzeigen'}
      </button>

      {offen && (
        <p className="mt-2 max-h-64 overflow-y-auto whitespace-pre-wrap rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
          {text || '(Kein Text hinterlegt.)'}
        </p>
      )}
    </div>
  );
}

// ─── Hauptkomponente ─────────────────────────────────────────────────────────

export default function ForderungsTagesbriefing() {
  const { isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<TagesberichtResponse | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('uebersicht');
  const [briefingVorschlag, setBriefingVorschlag] = useState('');
  const [feedback, setFeedback] = useState('');
  const [sending, setSending] = useState(false);
  const [sendingId, setSendingId] = useState<string | null>(null);
  // Session-Infos (Token + userId) für den „Alle Mails"-Tab, der eigene
  // API-Calls (Kommentar/Antwort/Versand) mit Bearer-Token durchführt.
  const [sessionInfo, setSessionInfo] = useState<{ token: string; userId: string } | null>(null);

  const authFetch = useCallback(async (init?: RequestInit) => {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData.session?.access_token;
    const userId = sessionData.session?.user?.id;
    if (!token) throw new Error('Nicht angemeldet');
    if (token && userId) setSessionInfo({ token, userId });
    return fetch('/api/forderung-tagesbericht', {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(init?.headers ?? {}),
      },
    });
  }, []);

  const ladeBericht = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch({ method: 'GET' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as TagesberichtResponse;
      setData(json);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  // Auto-Öffnen prüfen (einmalig beim Mount, nur wenn angemeldet UND Inhaber/Admin)
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        if (!sessionData.session) return;
        if (!shouldAutoOpen()) return;
        if (cancelled) return;
        setOpen(true);
        markShownToday();
        void ladeBericht();
      } catch {
        /* ignorieren */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ladeBericht, isAdmin]);

  // ESC schließt das Modal
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const handleSendFeedback = useCallback(async () => {
    if (!feedback.trim() && !briefingVorschlag.trim()) {
      toast.error('Bitte eine Rückmeldung eingeben');
      return;
    }
    setSending(true);
    try {
      const res = await authFetch({
        method: 'POST',
        body: JSON.stringify({
          feedback: feedback.trim() || '(nur Briefing-Anpassung)',
          briefing_vorschlag: briefingVorschlag.trim() || undefined,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Rückmeldung gespeichert, der Agent berücksichtigt sie beim nächsten Lauf');
      setFeedback('');
      setBriefingVorschlag('');
    } catch {
      toast.error('Rückmeldung konnte nicht gespeichert werden');
    } finally {
      setSending(false);
    }
  }, [authFetch, feedback, briefingVorschlag]);

  // „Freigeben & Senden" → POST /api/email-draft-send { id, userId } (Bearer-Token).
  const handleSendDraft = useCallback(async (id: string) => {
    if (!id) return;
    setSendingId(id);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      const userId = sessionData.session?.user?.id;
      if (!token || !userId) throw new Error('Nicht angemeldet');

      const res = await fetch('/api/email-draft-send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ id, userId }),
      });
      const json = (await res.json().catch(() => null)) as { ok?: boolean; error?: string } | null;
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || `HTTP ${res.status}`);
      }

      toast.success('Gesendet');
      // Eintrag aus der Freigabe-Liste entfernen.
      setData((prev) =>
        prev
          ? {
              ...prev,
              freigabe_vorschlaege: (prev.freigabe_vorschlaege ?? []).filter((v) => v.id !== id),
            }
          : prev,
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Senden fehlgeschlagen';
      toast.error(`Senden fehlgeschlagen: ${msg}`);
    } finally {
      setSendingId(null);
    }
  }, []);

  // Nur für den Inhaber/Admin sichtbar — für alle anderen nie rendern.
  if (!isAdmin) return null;
  if (!open) return null;

  const details: BerichtDetails = data?.bericht?.details ?? {};
  const live = data?.live_kennzahlen;
  const hinweis = data?.hinweis;
  const antwortVorschlaege = details.antwort_vorschlaege ?? [];
  const freigabeVorschlaege = data?.freigabe_vorschlaege ?? [];
  const versand = data?.versand_uebersicht;
  const versandEintraege = versand?.eintraege ?? [];
  const alleMails = data?.alle_mails ?? [];
  const optimierung = details.optimierungsvorschlaege ?? [];
  const einrichtung = details.einrichtungsvorschlaege ?? [];
  const entwicklung = details.entwicklungsvorschlaege ?? [];

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Tages-Assistent Forderungen"
    >
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => setOpen(false)}
      />

      {/* Panel */}
      <div className="relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 text-white"
          style={{ backgroundColor: BRAND }}
        >
          <div className="min-w-0">
            <h2 className="text-lg font-semibold leading-tight">Tages-Assistent · Forderungen</h2>
            <p className="mt-0.5 text-xs text-white/80">
              {data?.bericht?.erstellt_am
                ? `Bericht vom ${new Date(data.bericht.erstellt_am).toLocaleString('de-DE')}`
                : 'Buchhaltung & Forderungsmanagement'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-lg p-1.5 text-white/90 transition hover:bg-white/15"
            aria-label="Schließen"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Live-Kennzahlen */}
        {live && (
          <div className="grid grid-cols-2 gap-2 border-b border-gray-100 bg-gray-50 p-3 sm:grid-cols-4">
            <Kennzahl icon={Euro} label="Offene Forderungen" value={eur(live.offene_forderungen_summe)} />
            <Kennzahl
              icon={Inbox}
              label="Offene Akten"
              value={String(live.offene_forderungen_anzahl)}
            />
            <Kennzahl icon={Mail} label="Heute versendet" value={String(live.heute_versendet_anzahl)} />
            <Kennzahl
              icon={CheckCircle2}
              label="Zur Freigabe"
              value={String(live.ready_for_review_anzahl)}
            />
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 overflow-x-auto border-b border-gray-200 px-2 py-1.5">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = activeTab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setActiveTab(t.id)}
                className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                  active ? 'text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
                style={active ? { backgroundColor: BRAND } : undefined}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Inhalt (scrollbar) */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin" style={{ color: BRAND }} />
            </div>
          ) : (
            <>
              {hinweis && (
                <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{hinweis}</span>
                </div>
              )}

              {/* Tab: Übersicht / Sachstände */}
              {activeTab === 'uebersicht' && (
                <div>
                  {data?.bericht?.beschreibung && (
                    <p className="mb-4 whitespace-pre-wrap text-sm text-gray-700">
                      {data.bericht.beschreibung}
                    </p>
                  )}
                  <ListBlock titel="Sachstände" items={asList(details.sachstaende)} />
                  <ListBlock titel="Dokumente verbucht" items={asList(details.dokumente_verbucht)} />
                  <ListBlock
                    titel="Offene Fragen an Andreas"
                    items={asList(details.offene_fragen_an_andreas)}
                  />
                  {asList(details.sachstaende).length === 0 &&
                    asList(details.dokumente_verbucht).length === 0 &&
                    asList(details.offene_fragen_an_andreas).length === 0 &&
                    !data?.bericht?.beschreibung && (
                      <p className="py-8 text-center text-sm text-gray-400">
                        Keine Sachstände im aktuellen Bericht.
                      </p>
                    )}
                </div>
              )}

              {/* Tab: Antwort-Vorschläge (freigabebereite Entwürfe + Agenten-Vorschläge) */}
              {activeTab === 'antworten' && (
                <div className="space-y-6">
                  {/* Freigabebereite E-Mail-Entwürfe — direkt versendbar */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4" style={{ color: BRAND }} />
                      <h3 className="text-sm font-semibold text-gray-900">
                        Freigabebereite E-Mails
                      </h3>
                      {freigabeVorschlaege.length > 0 && (
                        <span
                          className="rounded-full px-2 py-0.5 text-[11px] font-semibold text-white"
                          style={{ backgroundColor: BRAND }}
                        >
                          {freigabeVorschlaege.length}
                        </span>
                      )}
                    </div>
                    {freigabeVorschlaege.length === 0 ? (
                      <p className="rounded-lg bg-gray-50 py-6 text-center text-sm text-gray-400">
                        Aktuell keine freigabebereiten E-Mails.
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {freigabeVorschlaege.map((v) => (
                          <FreigabeKarte
                            key={v.id}
                            vorschlag={v}
                            onSend={handleSendDraft}
                            sending={sendingId === v.id}
                          />
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Agenten-Antwort-Vorschläge (Text + Strategien) */}
                  {antwortVorschlaege.length > 0 && (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <MessageSquareReply className="h-4 w-4" style={{ color: BRAND }} />
                        <h3 className="text-sm font-semibold text-gray-900">
                          Vorschläge des Agenten
                        </h3>
                      </div>
                      {antwortVorschlaege.map((v, i) => (
                        <div key={i} className="rounded-xl border border-gray-200 p-4">
                          <div className="mb-2">
                            <div className="text-sm font-semibold text-gray-900">
                              {v.betreff || 'Ohne Betreff'}
                            </div>
                            {v.absender && (
                              <div className="text-xs text-gray-500">Von: {v.absender}</div>
                            )}
                          </div>
                          {v.vorschlag_text && (
                            <p className="mb-3 whitespace-pre-wrap rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
                              {v.vorschlag_text}
                            </p>
                          )}
                          {Array.isArray(v.strategien) && v.strategien.length > 0 && (
                            <div className="space-y-2">
                              <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                                Strategien
                              </div>
                              {v.strategien.map((s, j) => (
                                <div
                                  key={j}
                                  className={`rounded-lg border p-2.5 text-sm ${
                                    s.empfohlen
                                      ? 'border-transparent'
                                      : 'border-gray-200 bg-white text-gray-700'
                                  }`}
                                  style={
                                    s.empfohlen
                                      ? { backgroundColor: `${BRAND}0F`, borderColor: `${BRAND}55` }
                                      : undefined
                                  }
                                >
                                  <div className="flex items-center gap-2">
                                    {s.empfohlen && (
                                      <span
                                        className="rounded px-1.5 py-0.5 text-[10px] font-bold uppercase text-white"
                                        style={{ backgroundColor: BRAND }}
                                      >
                                        Empfohlen
                                      </span>
                                    )}
                                    {s.label && (
                                      <span className="font-medium text-gray-900">{s.label}</span>
                                    )}
                                  </div>
                                  {s.text && (
                                    <p className="mt-1 whitespace-pre-wrap text-gray-700">{s.text}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {freigabeVorschlaege.length === 0 && antwortVorschlaege.length === 0 && (
                    <p className="py-4 text-center text-sm text-gray-400">
                      Keine Antwort-Vorschläge vorhanden.
                    </p>
                  )}
                </div>
              )}

              {/* Tab: Alle Mails (eingehende Mails + Kommentare + Antwort-Generator) */}
              {activeTab === 'alle-mails' && (
                <>
                  {sessionInfo ? (
                    <AlleMailsTab
                      mails={alleMails}
                      userId={sessionInfo.userId}
                      authToken={sessionInfo.token}
                    />
                  ) : (
                    <p className="py-8 text-center text-sm text-gray-400">
                      Sitzung wird geladen …
                    </p>
                  )}
                </>
              )}

              {/* Tab: Pipeline */}
              {activeTab === 'pipeline' && (
                <div>
                  <ListBlock titel="Gestern versandt" items={asList(details.gestern_versandt)} />
                  <ListBlock titel="Heute geplant" items={asList(details.heute_geplant)} />
                  {details.pipeline_kommentar && (
                    <div className="mb-2">
                      <h4 className="mb-1.5 text-sm font-semibold text-gray-900">
                        Kommentar / Was fehlt
                      </h4>
                      <p className="whitespace-pre-wrap rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
                        {details.pipeline_kommentar}
                      </p>
                    </div>
                  )}
                  {asList(details.gestern_versandt).length === 0 &&
                    asList(details.heute_geplant).length === 0 &&
                    !details.pipeline_kommentar && (
                      <p className="py-8 text-center text-sm text-gray-400">
                        Keine Pipeline-Informationen im aktuellen Bericht.
                      </p>
                    )}
                </div>
              )}

              {/* Tab: Versand (kurze Übersicht: gestern/heute rausgegangen) */}
              {activeTab === 'versand' && (
                <div>
                  <div className="mb-4 flex flex-wrap gap-2">
                    <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm">
                      <span className="text-gray-500">Gestern versendet:</span>
                      <span className="font-semibold text-gray-900">
                        {versand?.gestern_anzahl ?? 0}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm">
                      <span className="text-gray-500">Heute versendet:</span>
                      <span className="font-semibold text-gray-900">
                        {versand?.heute_anzahl ?? 0}
                      </span>
                    </div>
                  </div>

                  {versandEintraege.length === 0 ? (
                    <p className="py-8 text-center text-sm text-gray-400">
                      Seit gestern wurden keine E-Mails versendet.
                    </p>
                  ) : (
                    <ul className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200">
                      {versandEintraege.map((e, i) => (
                        <li key={i} className="flex items-center gap-3 px-3 py-2.5">
                          <Mail className="h-4 w-4 shrink-0 text-gray-400" />
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium text-gray-900">
                              {e.subject || 'Ohne Betreff'}
                            </div>
                            <div className="truncate text-xs text-gray-500">
                              {e.recipient || 'Unbekannt'}
                            </div>
                          </div>
                          {uhrzeit(e.sent_at) && (
                            <span className="flex shrink-0 items-center gap-1 text-xs text-gray-400">
                              <Clock className="h-3 w-3" />
                              {uhrzeit(e.sent_at)}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {/* Tab: Briefing */}
              {activeTab === 'briefing' && (
                <div>
                  <h4 className="mb-1.5 text-sm font-semibold text-gray-900">Aktuelles Briefing</h4>
                  <p className="mb-4 whitespace-pre-wrap rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
                    {details.briefing || 'Kein Briefing im aktuellen Bericht hinterlegt.'}
                  </p>
                  <label
                    htmlFor="briefing-vorschlag"
                    className="mb-1.5 block text-sm font-semibold text-gray-900"
                  >
                    Anpassung vorschlagen
                  </label>
                  <textarea
                    id="briefing-vorschlag"
                    value={briefingVorschlag}
                    onChange={(e) => setBriefingVorschlag(e.target.value)}
                    rows={4}
                    placeholder="Wie soll das Briefing des Agenten angepasst werden?"
                    className="w-full resize-y rounded-lg border border-gray-300 p-3 text-sm outline-none focus:border-gray-400 focus:ring-2 focus:ring-red-100"
                  />
                  <p className="mt-1 text-xs text-gray-400">
                    Wird zusammen mit der Rückmeldung unten an den Agenten übermittelt.
                  </p>
                </div>
              )}

              {/* Tab: Optimierung & Entwicklung */}
              {activeTab === 'entwicklung' && (
                <div>
                  {[
                    { titel: 'Optimierungsvorschläge', items: optimierung },
                    { titel: 'Einrichtungsvorschläge', items: einrichtung },
                    { titel: 'Entwicklungsvorschläge', items: entwicklung },
                  ].map((block) =>
                    block.items.length > 0 ? (
                      <div key={block.titel} className="mb-4">
                        <h4 className="mb-1.5 text-sm font-semibold text-gray-900">{block.titel}</h4>
                        <ul className="space-y-2">
                          {block.items.map((v, i) => (
                            <li key={i} className="rounded-lg border border-gray-200 p-3">
                              <div className="text-sm font-medium text-gray-900">
                                {vorschlagTitel(v)}
                              </div>
                              {vorschlagText(v) && (
                                <p className="mt-1 whitespace-pre-wrap text-sm text-gray-600">
                                  {vorschlagText(v)}
                                </p>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null,
                  )}
                  {optimierung.length === 0 &&
                    einrichtung.length === 0 &&
                    entwicklung.length === 0 && (
                      <p className="py-8 text-center text-sm text-gray-400">
                        Keine Optimierungs- oder Entwicklungsvorschläge im aktuellen Bericht.
                      </p>
                    )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer: Rückmeldung an den Agenten */}
        <div className="border-t border-gray-200 bg-gray-50 p-4">
          <label htmlFor="agent-feedback" className="mb-1.5 block text-sm font-semibold text-gray-900">
            Rückmeldung an den Agenten
          </label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <textarea
              id="agent-feedback"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={2}
              placeholder="Was soll der Agent beim nächsten Lauf beachten?"
              className="flex-1 resize-y rounded-lg border border-gray-300 p-2.5 text-sm outline-none focus:border-gray-400 focus:ring-2 focus:ring-red-100"
            />
            <button
              type="button"
              onClick={handleSendFeedback}
              disabled={sending}
              className="flex h-fit items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium text-white transition disabled:opacity-60"
              style={{ backgroundColor: BRAND }}
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              Senden
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
